import { PUBLIC_SITE_ORIGIN } from "@/lib/public-site-origin";
import { merchantListingOfferFields } from "@/lib/seo/merchant-listing";
import { SEO_WEIGHTED_V2 } from "@/lib/seo-audit-score";

// Client-safe product contract. Stripe prices must match these display amounts.
export const SEO_PRICING = {
  free: { label: "Free", gbp: 0 },
  fixPack: {
    label: "Full Fix Pack",
    gbp: 39,
    interval: "per URL",
    reports: 1,
    urls: 1,
    verifications: 1,
    verificationWindowDays: 30,
  },
  starter: {
    label: "Starter",
    gbp: 29,
    annualGbp: 290,
    interval: "month",
    sites: 1,
    manualAudits: null,
    scheduledAudits: 2,
  },
  foundingMember: {
    label: "Founding Member",
    gbp: 299,
    interval: "one-time lifetime",
  },
  pro: {
    label: "Pro",
    gbp: 59,
    annualGbp: 590,
    interval: "month",
    sites: 1,
    manualAudits: null,
    scheduledAudits: 4,
  },
  team: {
    label: "Business",
    // Live Stripe Business pinned at £129/mo (£1,190/yr). Published on /resources/pricing
    // + JSON-LD when checkout env/flags allow. Caps below feed entitlements.
    gbp: 129,
    annualGbp: 1190,
    interval: "month",
    sites: 1,
    manualAudits: null,
    scheduledAudits: 20,
  },
  agency: { label: "Agency", gbp: 149, interval: "month", availability: "waitlist" },
} as const;

/** Real annual saving: annual price versus twelve monthly payments, rounded. */
export function annualSavingPercent(gbp: number, annualGbp: number): number {
  return Math.round((1 - annualGbp / (gbp * 12)) * 100);
}

/** Customer-facing name for an access tier key (`team` is sold as Business). */
export function planTierLabel(tier: string | null | undefined): string | null {
  return tier === "free" || tier === "starter" || tier === "pro" || tier === "team"
    ? SEO_PRICING[tier].label
    : null;
}

/**
 * One honest duration phrase for public copy. An audit runs mobile and desktop
 * Lighthouse plus HTML checks; most finish in roughly 45 to 75 seconds and the
 * inline engine allows up to INLINE_AUDIT_TIMEOUT_MS for slow sites.
 */
export const AUDIT_DURATION_COPY = "about a minute";

export const SEO_FREE_ALLOWANCE = {
  anonymousAudits: 1,
  signedInAuditsPerMonth: 3,
} as const;

export const SEO_PLAN_LIMITS = {
  anonymous: { manualAudits: SEO_FREE_ALLOWANCE.anonymousAudits, window: "rolling 30 days" },
  free: { manualAudits: SEO_FREE_ALLOWANCE.signedInAuditsPerMonth, window: "UTC month" },
  fixPack: {
    reports: SEO_PRICING.fixPack.reports,
    urls: SEO_PRICING.fixPack.urls,
    verifications: SEO_PRICING.fixPack.verifications,
    verificationWindowDays: SEO_PRICING.fixPack.verificationWindowDays,
  },
  starter: {
    manualAudits: SEO_PRICING.starter.manualAudits,
    scheduledAudits: SEO_PRICING.starter.scheduledAudits,
    sites: SEO_PRICING.starter.sites,
    window: "UTC month",
  },
  pro: {
    manualAudits: SEO_PRICING.pro.manualAudits,
    scheduledAudits: SEO_PRICING.pro.scheduledAudits,
    sites: SEO_PRICING.pro.sites,
    window: "UTC month",
  },
  team: {
    manualAudits: SEO_PRICING.team.manualAudits,
    scheduledAudits: SEO_PRICING.team.scheduledAudits,
    sites: SEO_PRICING.team.sites,
    window: "UTC month",
  },
} as const;

export type PublicOfferStatus = Readonly<{
  fixPackAvailable: boolean;
  starterAvailable: boolean;
  proAvailable: boolean;
  teamAvailable: boolean;
  starterMonthlyAvailable: boolean;
  starterAnnualAvailable: boolean;
  proMonthlyAvailable: boolean;
  proAnnualAvailable: boolean;
  teamMonthlyAvailable: boolean;
  teamAnnualAvailable: boolean;
  portalAvailable: boolean;
}>;

/**
 * Machine-readable pricing parity for /resources/pricing. Every Offer mirrors
 * the visible public offer: Free is always available; Fix Pack and live Pro
 * flip between InStock and OutOfStock with checkout state; Founding Member is
 * a separate one-time Payment Link. Team is included when Team checkout is live.
 * Agency is waitlist-only and omitted because schema must not imply an
 * active checkout price.
 */
export function generatePricingJsonLd(
  status: Pick<PublicOfferStatus, "fixPackAvailable" | "starterAvailable" | "proAvailable" | "teamAvailable">,
  origin = PUBLIC_SITE_ORIGIN,
) {
  const availability = (onSale: boolean) =>
    onSale ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";
  const provider = { "@type": "Organization", name: "HubSEO", url: origin };
  const merchant = merchantListingOfferFields(origin);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "HubSEO SEO pricing",
    url: `${origin}/resources/pricing`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: [
        {
          "@type": "Product",
          name: "HubSEO SEO Audit (Free)",
          description:
            "One anonymous audit per rolling 30 days; three signed-in audits per UTC month. Measured Lighthouse lab evidence and ranked fixes.",
          brand: provider,
          offers: {
            "@type": "Offer",
            price: SEO_PRICING.free.gbp,
            priceCurrency: "GBP",
            availability: "https://schema.org/InStock",
            url: `${origin}/seo-audit`,
            ...merchant,
          },
        },
        {
          "@type": "Product",
          name: `HubSEO ${SEO_PRICING.fixPack.label}`,
          description: `Complete saved report for one URL with one verification within ${SEO_PRICING.fixPack.verificationWindowDays} days.`,
          brand: provider,
          offers: {
            "@type": "Offer",
            price: SEO_PRICING.fixPack.gbp,
            priceCurrency: "GBP",
            availability: availability(status.fixPackAvailable),
            url: `${origin}/resources/pricing`,
            ...merchant,
          },
        },
        {
          "@type": "Product",
          name: `HubSEO ${SEO_PRICING.starter.label}`,
          description: `Manual audits while ${SEO_PRICING.starter.label} is active, ${SEO_PRICING.starter.scheduledAudits} scheduled audits per UTC month for ${SEO_PRICING.starter.sites} site, and saved report history with full tools while ${SEO_PRICING.starter.label} is active.`,
          brand: provider,
          offers: {
            "@type": "Offer",
            price: SEO_PRICING.starter.gbp,
            priceCurrency: "GBP",
            availability: availability(status.starterAvailable),
            url: `${origin}/pricing/starter`,
            ...merchant,
          },
        },
        {
          "@type": "Product",
          name: `HubSEO ${SEO_PRICING.pro.label}`,
          description:
            "Manual audits while Pro is active, four scheduled audits per UTC month for one site, and saved report history with full tools while Pro is active.",
          brand: provider,
          offers: {
            "@type": "Offer",
            price: SEO_PRICING.pro.gbp,
            priceCurrency: "GBP",
            availability: availability(status.proAvailable),
            url: `${origin}/resources/pricing`,
            ...merchant,
          },
        },
        {
          "@type": "Product",
          name: `HubSEO ${SEO_PRICING.team.label}`,
          description:
            "Manual audits while Business is active, scheduled audits, saved report history and full tools while Business is active.",
          brand: provider,
          offers: {
            "@type": "Offer",
            price: SEO_PRICING.team.gbp,
            priceCurrency: "GBP",
            availability: availability(status.teamAvailable),
            url: `${origin}/resources/pricing`,
            ...merchant,
          },
        },
        {
          "@type": "Product",
          name: `HubSEO ${SEO_PRICING.foundingMember.label}`,
          description:
            "One-time lifetime price for manual SEO audits with measured evidence, ranked fixes, and AI-ready prompts. Scheduled audits are not included.",
          brand: provider,
          offers: {
            "@type": "Offer",
            price: SEO_PRICING.foundingMember.gbp,
            priceCurrency: "GBP",
            availability: "https://schema.org/InStock",
            url: `${origin}/founding-member`,
            ...merchant,
          },
        },
      ],
    },
  };
}

export const SEO_ACTIVE_PRO_COPY = "Saved full-report history; full tools while Pro is active";

export const SEO_ACTIVE_PAID_COPY = "Saved full-report history; full tools while paid access is active";

export const SEO_PRICING_FEATURES = {
  free: [
    `${SEO_PLAN_LIMITS.anonymous.manualAudits} anonymous audit per ${SEO_PLAN_LIMITS.anonymous.window}`,
    `${SEO_PLAN_LIMITS.free.manualAudits} signed-in audits per ${SEO_PLAN_LIMITS.free.window}`,
    "Site audit score or honest unscored result",
  ],
  fixPack: [
    `${SEO_PLAN_LIMITS.fixPack.reports} complete saved report for ${SEO_PLAN_LIMITS.fixPack.urls} URL`,
    "Evidence-backed fixes, implementation prompts, and download bundle",
    `${SEO_PLAN_LIMITS.fixPack.verifications} verification within ${SEO_PLAN_LIMITS.fixPack.verificationWindowDays} days of purchase`,
  ],
  starter: [
    "Manual audits while Starter is active",
    `${SEO_PLAN_LIMITS.starter.scheduledAudits} scheduled audits per ${SEO_PLAN_LIMITS.starter.window} for ${SEO_PLAN_LIMITS.starter.sites} site`,
    "Saved full-report history; full tools while Starter is active",
  ],
  foundingMember: [
    "One-time lifetime price",
    "Measured SEO audit with labelled evidence",
    "Ranked fixes and AI-ready prompts",
  ],
  pro: [
    "Manual audits while Pro is active",
    `${SEO_PLAN_LIMITS.pro.scheduledAudits} scheduled audits per ${SEO_PLAN_LIMITS.pro.window} for ${SEO_PLAN_LIMITS.pro.sites} site`,
    SEO_ACTIVE_PRO_COPY,
  ],
  team: [
    "Manual audits while Business is active",
    `${SEO_PLAN_LIMITS.team.scheduledAudits} scheduled audits per ${SEO_PLAN_LIMITS.team.window} for ${SEO_PLAN_LIMITS.team.sites} site`,
    "Saved full-report history; full tools while Business is active",
  ],
} as const;

export function getSeoAuditSchemaOffers(
  status: Pick<PublicOfferStatus, "fixPackAvailable">,
  origin = PUBLIC_SITE_ORIGIN,
) {
  const merchant = merchantListingOfferFields(origin);
  return [
    {
      "@type": "Offer",
      name: SEO_PRICING.free.label,
      price: String(SEO_PRICING.free.gbp),
      priceCurrency: "GBP",
      url: `${origin}/seo-audit`,
      ...merchant,
    },
    ...(status.fixPackAvailable
      ? [{
          "@type": "Offer",
          name: SEO_PRICING.fixPack.label,
          price: String(SEO_PRICING.fixPack.gbp),
          priceCurrency: "GBP",
          url: `${origin}/resources/pricing`,
          ...merchant,
        }]
      : []),
    {
      "@type": "Offer",
      name: SEO_PRICING.foundingMember.label,
      price: String(SEO_PRICING.foundingMember.gbp),
      priceCurrency: "GBP",
      url: `${origin}/founding-member`,
      ...merchant,
    },
  ];
}

export type PublicFaqItem = {
  question: string;
  answer: string;
};

/** Shared Fix Pack FAQ. Off-state must not quote the Fix Pack price or sell a current verification. */
export function getPublicFixPackFaqCopy(
  status: Pick<PublicOfferStatus, "fixPackAvailable">,
): PublicFaqItem {
  return {
    question: "What is in the Fix Pack?",
    answer: status.fixPackAvailable
      ? `The full report and the evidence-backed fixes for that URL. You get one verification within ${SEO_PRICING.fixPack.verificationWindowDays} days. The Full Fix Pack is £${SEO_PRICING.fixPack.gbp} per URL.`
      : "The full report and the evidence-backed fixes for that URL. You get one verification within 30 days. We’re not selling it right now.",
  };
}

/** Shared signup FAQ. Off-state keeps Founding Member as the live paid mention. */
export function getPublicSignupFaqCopy(
  status: Pick<PublicOfferStatus, "fixPackAvailable">,
): PublicFaqItem {
  const free =
    "No. Anonymous visitors get one free audit per 30 days and a shareable report link plus PDF. Signed-in free accounts get three audits per UTC month and a private history of their free audit results.";
  return {
    question: "Do I need to sign up?",
    answer: status.fixPackAvailable
      ? `${free} Fix Pack (£${SEO_PRICING.fixPack.gbp} per URL) keeps the report, unlocks the full fix inventory, and includes one verification within ${SEO_PRICING.fixPack.verificationWindowDays} days.`
      : `${free} We’re not selling the Fix Pack right now.`,
  };
}

export function getHomePublicFaqs(
  status: Pick<PublicOfferStatus, "fixPackAvailable">,
): readonly PublicFaqItem[] {
  return [
    {
      question: "Is the SEO audit really free?",
      answer:
        "Yes. Run one audit without an account or card and see your score, Core Web Vitals, and desktop benchmark.",
    },
    {
      question: "What does the audit check?",
      answer:
        "It checks mobile and desktop performance, SEO, accessibility, best practices, and Core Web Vitals using Google Lighthouse data.",
    },
    {
      question: "Do you store or sell my data?",
      answer:
        "We never sell your data. We keep only the minimum report data needed to show and share your audit.",
    },
    getPublicFixPackFaqCopy(status),
    {
      question: "How long does an audit take?",
      answer:
        `Most audits finish in ${AUDIT_DURATION_COPY}. Large or slow sites can take longer while browser checks complete.`,
    },
  ];
}

export function getSeoAuditPublicFaqs(
  status: Pick<PublicOfferStatus, "fixPackAvailable">,
): readonly PublicFaqItem[] {
  return [
    {
      question: "What does the free SEO audit check?",
      answer:
        "The audit captures mobile and desktop Lighthouse lab metrics, Core Web Vitals (LCP, CLS, INP, TTFB), HTML evidence, security headers, crawlability (robots.txt, sitemap.xml, redirects), on-page signals (title, meta description, headings, canonical, structured data), and an experimental AI-readiness checklist. Every finding names its source; missing evidence is marked unavailable rather than guessed.",
    },
    {
      question: "How long does the audit take?",
      answer:
        `Most public pages complete in ${AUDIT_DURATION_COPY} for mobile and desktop Lighthouse captures and zero-API-cost SEO checks. Larger or slower targets take longer. You can cancel and retry at any time.`,
    },
    getPublicSignupFaqCopy(status),
    {
      question: "How is the score calculated?",
      answer:
        `${SEO_WEIGHTED_V2.description} Desktop scores are shown separately. Zero-API-cost checks are presented as pass/warning/fail and do not change the overall score.`,
    },
    {
      question: "What’s the difference between lab and field data?",
      answer:
        "Lab data is measured in a controlled Chromium session at audit time. Field data comes from the Chrome UX Report (CrUX) when available and reflects real-user p75 over the last 28 days. Both are shown when the URL has enough CrUX volume; otherwise lab data is used.",
    },
  ];
}

export function getPricingPageMetadata(
  status: Pick<PublicOfferStatus, "fixPackAvailable" | "proAvailable" | "teamAvailable">,
) {
  const recurring = [
    status.proAvailable ? `£${SEO_PRICING.pro.gbp}/mo Pro` : null,
    status.teamAvailable ? `£${SEO_PRICING.team.gbp}/mo Business` : null,
  ].filter(Boolean);
  const recurringPhrase = recurring.length > 0 ? `, ${recurring.join(", ")}` : "";

  if (status.fixPackAvailable) {
    return {
      title: "SEO Pricing: Free Audit, Fix Pack, Pro, Business | HubSEO",
      description:
        `Compare HubSEO SEO pricing: free audit, £${SEO_PRICING.fixPack.gbp} Full Fix Pack${recurringPhrase}, £${SEO_PRICING.foundingMember.gbp} Founding Member, and Agency waitlist.`,
      ogTitle: "SEO Pricing: Free Audit, Fix Pack, Pro & Business",
      ogDescription:
        `See HubSEO’s free audit, £${SEO_PRICING.fixPack.gbp} Full Fix Pack${recurringPhrase}, £${SEO_PRICING.foundingMember.gbp} Founding Member, and Agency waitlist.`,
      twitterDescription:
        "Compare HubSEO’s free audit, Fix Pack, Pro, Business, Founding Member, and Agency waitlist.",
      keywords: [
        "SEO toolkit pricing",
        "SEO software pricing",
        "SEO audit tool pricing",
        "HubSEO pricing",
        "SEO Fix Pack pricing",
        "HubSEO Pro",
        "HubSEO Business",
      ],
    };
  }
  return {
    title: "SEO Pricing: Free Audit, Pro, Business & Founding Member | HubSEO",
    description:
      `Compare HubSEO SEO pricing: free audit${recurringPhrase}, £${SEO_PRICING.foundingMember.gbp} Founding Member lifetime offer, and Agency waitlist.`,
    ogTitle: "SEO Pricing: Free Audit, Pro, Business & Founding Member",
    ogDescription:
      `See HubSEO’s free audit${recurringPhrase}, £${SEO_PRICING.foundingMember.gbp} Founding Member lifetime offer, and Agency waitlist.`,
    twitterDescription:
      "Compare HubSEO’s free audit, Pro, Business, Founding Member, and Agency waitlist.",
    keywords: [
      "SEO toolkit pricing",
      "SEO software pricing",
      "SEO audit tool pricing",
      "HubSEO pricing",
      "HubSEO Pro",
      "HubSEO Business",
    ],
  };
}

export const SEO_VOCABULARY = {
  product: "SEO",
  report: "Report",
  fixPack: "Fix Pack",
  freshAudit: "Run fresh audit",
  verify: "Verify fixes",
  pixel: "HubSEO Pixel (beta)",
  states: ["Proposed", "Copied", "Verified", "Regressed", "Needs review"],
} as const;

export const SEO_FEATURE_FLAGS = {
  fixPack: process.env.NEXT_PUBLIC_SEO_FIX_PACK_ENABLED === "true",
  verification: process.env.NEXT_PUBLIC_SEO_VERIFICATION_ENABLED === "true",
  scheduledAudits: process.env.NEXT_PUBLIC_SEO_SCHEDULED_AUDITS_ENABLED === "true",
  pixelBeta: process.env.NEXT_PUBLIC_SEO_PIXEL_BETA_ENABLED === "true",
  fixPrompt: process.env.NEXT_PUBLIC_SEO_FIX_PROMPT_ENABLED === "true",
} as const;

// Compatibility for existing audit UI while consumers move to SEO_PRICING.
export const SEO_AUDIT_PRICES = {
  oneOffReport: SEO_PRICING.fixPack,
  proMonthly: SEO_PRICING.pro,
} as const;
